import { useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Phone, Search, UserPlus, Loader2 } from 'lucide-react';
import {
  pickContacts,
  parseManualPhones,
  storeContacts,
} from '../../services/contactImport';
import { matchContactsToUsers } from '../../services/supabaseService';
import { Capacitor } from '@capacitor/core';
import { getAirline } from '../../utils/airlines';
import type { User } from '../../types';

type Phase = 'idle' | 'searching' | 'results';

export function ContactImport() {
  const { currentUser, friends, sendFriendRequest } = useApp();

  const [phase, setPhase] = useState<Phase>('idle');
  const [manualInput, setManualInput] = useState('');
  const [matchedUsers, setMatchedUsers] = useState<User[]>([]);
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const isNative = Capacitor.isNativePlatform();

  const runMatching = useCallback(async () => {
    setPhase('searching');
    setError(null);

    const matches = await matchContactsToUsers(currentUser.id);
    const newMatches = matches.filter(
      (u) => !friends.some((f) => f.id === u.id)
    );
    setMatchedUsers(newMatches);
    setPhase('results');
  }, [currentUser.id, friends]);

  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);

  const handlePickContacts = useCallback(async () => {
    setError(null);
    setShowPermissionPrompt(false);
    const contacts = await pickContacts();
    if (!contacts) {
      setError('No contacts selected or permission denied.');
      return;
    }
    await storeContacts(currentUser.id, contacts, currentUser.phone || undefined);
    await runMatching();
  }, [currentUser.id, currentUser.phone, runMatching]);

  const handleManualSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      const contacts = parseManualPhones(manualInput);
      if (contacts.length === 0) {
        setError('Enter at least one valid phone number (7+ digits).');
        return;
      }
      await storeContacts(currentUser.id, contacts, currentUser.phone || undefined);
      await runMatching();
    },
    [manualInput, currentUser.id, currentUser.phone, runMatching]
  );

  const handleAddFriend = useCallback(
    async (user: User) => {
      const err = await sendFriendRequest(user.handle);
      if (!err) {
        setSentRequests((prev) => new Set(prev).add(user.id));
      }
    },
    [sendFriendRequest]
  );

  const handleReset = useCallback(() => {
    setPhase('idle');
    setMatchedUsers([]);
    setManualInput('');
    setError(null);
    setSentRequests(new Set());
  }, []);

  // ── Searching state ──
  if (phase === 'searching') {
    return (
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700/50">
        <div className="flex items-center justify-center gap-3 py-6">
          <Loader2 size={20} className="text-slate-400 animate-spin" />
          <span className="text-sm text-slate-300">
            Searching for crew members in your contacts...
          </span>
        </div>
      </div>
    );
  }

  // ── Results state ──
  if (phase === 'results') {
    return (
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700/50">
        {matchedUsers.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-slate-400">
              No crew members found in your contacts.
            </p>
            <button
              onClick={handleReset}
              className="mt-3 text-xs text-slate-500 hover:text-slate-300 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Search size={14} className="text-coral" />
              <span className="text-xs font-semibold text-coral uppercase tracking-wide">
                Found in your contacts
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {matchedUsers.map((user) => {
                const airline = getAirline(user.airlineCode);
                const alreadySent = sentRequests.has(user.id);
                return (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-slate-750 border border-slate-700/30"
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                      style={{
                        backgroundColor: airline.color,
                        color: airline.textOnColor,
                      }}
                    >
                      {user.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-100">
                          {user.name}
                        </span>
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                          style={{
                            backgroundColor: airline.color,
                            color: airline.textOnColor,
                          }}
                        >
                          {airline.name}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500">
                        @{user.handle}
                      </div>
                    </div>
                    {alreadySent ? (
                      <span className="text-xs text-sage font-medium px-2 py-1">
                        Sent!
                      </span>
                    ) : (
                      <button
                        onClick={() => handleAddFriend(user)}
                        className="flex items-center gap-1 text-xs font-medium text-white bg-coral/90 hover:bg-coral px-2.5 py-1 rounded-lg transition-colors"
                      >
                        <UserPlus size={12} />
                        Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              onClick={handleReset}
              className="mt-3 w-full text-xs text-slate-500 hover:text-slate-300 transition-colors text-center py-1"
            >
              Search again
            </button>
          </>
        )}
      </div>
    );
  }

  // ── Idle state ──
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700/50">
      {isNative ? (
        showPermissionPrompt ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-300">
              We'll match your contacts to find crew on Rost & Found. Phone numbers are <strong className="text-slate-200">hashed on your device</strong> — raw numbers never leave your phone. Hashes are kept to notify you when contacts join later. You can clear this data anytime in Settings.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPermissionPrompt(false)}
                className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-400 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePickContacts}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent-dark text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Phone size={15} />
                Allow
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowPermissionPrompt(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors"
          >
            <Phone size={15} />
            Share Contacts
          </button>
        )
      ) : (
        <div className="text-center">
          <p className="text-xs text-slate-500 mb-3">
            Enter phone numbers below to find crew members.
          </p>
        </div>
      )}

      {isNative && (
        <div className="flex items-center gap-3 my-3">
          <div className="flex-1 border-t border-slate-700/50" />
          <span className="text-xs text-slate-600 uppercase tracking-wider">
            or enter manually
          </span>
          <div className="flex-1 border-t border-slate-700/50" />
        </div>
      )}

      <form onSubmit={handleManualSubmit}>
        <textarea
          value={manualInput}
          onChange={(e) => {
            setManualInput(e.target.value);
            setError(null);
          }}
          placeholder="Enter phone numbers (comma or newline separated)"
          rows={2}
          className="w-full px-3 py-2 text-sm border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-slate-700 text-slate-100 placeholder-slate-500 resize-none"
        />
        <button
          type="submit"
          className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors"
        >
          <Search size={14} />
          Find Crew Members
        </button>
      </form>

      {error && (
        <p className="mt-2 text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}
